import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  seedAdmin: () => api.post('/auth/seed-admin'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

export const appealsAPI = {
  create: (data) => api.post('/appeals', data),
  list: (params) => api.get('/appeals', { params }),
  get: (id) => api.get(`/appeals/${id}`),
  updateStatus: (id, status) => api.put(`/appeals/${id}/status`, { status }),
  delete: (id) => api.delete(`/appeals/${id}`),
  addNote: (id, content) => api.post(`/appeals/${id}/notes`, { content }),
};

export const plansAPI = {
  list: (activeOnly = true) => api.get('/plans', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/plans', data),
  update: (id, data) => api.put(`/plans/${id}`, data),
  delete: (id) => api.delete(`/plans/${id}`),
};

export const paymentsAPI = {
  checkout: (data) => api.post('/payments/checkout', data),
  submitFree: (appealId) => api.post(`/payments/free/${appealId}`),
  status: (sessionId) => api.get(`/payments/status/${sessionId}`),
  history: () => api.get('/payments/history'),
};

export const trackingAPI = {
  track: (code) => api.get(`/track/${code}`),
};

export const adminAPI = {
  stats: () => api.get('/admin/stats'),
  users: (params) => api.get('/admin/users', { params }),
  createUser: (data) => api.post('/admin/users', data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  blockUser: (id) => api.put(`/admin/users/${id}/block`),
  updateRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
  getUserDetail: (id) => api.get(`/admin/users/${id}/detail`),
  updateUserProfile: (id, data) => api.put(`/admin/users/${id}/profile`, data),
  exportAppeals: (format) => api.get('/admin/export/appeals', { params: { format }, responseType: format === 'excel' ? 'blob' : 'json' }),
  analytics: () => api.get('/admin/analytics'),
  activityLog: (params) => api.get('/admin/activity-log', { params }),
};

export const subscriptionsAPI = {
  me: () => api.get('/subscriptions/me'),
};

export const suggestionsAPI = {
  list: (activeOnly = true) => api.get('/suggestions', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/suggestions', data),
  update: (id, data) => api.put(`/suggestions/${id}`, data),
  delete: (id) => api.delete(`/suggestions/${id}`),
};

export const platformFieldsAPI = {
  list: (platform) => api.get('/platform-fields', { params: platform ? { platform } : {} }),
  create: (data) => api.post('/platform-fields', data),
  update: (id, data) => api.put(`/platform-fields/${id}`, data),
  delete: (id) => api.delete(`/platform-fields/${id}`),
};

export const affiliateAPI = {
  stats: () => api.get('/affiliate/stats'),
};

export const contactAPI = {
  send: (data) => api.post('/contact', data),
};

export const settingsAPI = {
  get: () => api.get('/admin/settings'),
  update: (data) => api.put('/admin/settings', data),
  getBranding: () => api.get('/admin/branding'),
  updateBranding: (data) => api.put('/admin/branding', data),
  getPaymentGateways: () => api.get('/admin/payment-gateways'),
  updatePaymentGateway: (id, data) => api.put(`/admin/payment-gateways/${id}`, data),
  getApiKeys: () => api.get('/admin/api-keys'),
  addApiKey: (data) => api.post('/admin/api-keys', data),
  deleteApiKey: (id) => api.delete(`/admin/api-keys/${id}`),
  getBlocklist: () => api.get('/admin/blocklist'),
  addToBlocklist: (data) => api.post('/admin/blocklist', data),
  removeFromBlocklist: (id) => api.delete(`/admin/blocklist/${id}`),
  contacts: () => api.get('/admin/contacts'),
  deleteContact: (id) => api.delete(`/admin/contacts/${id}`),
  affiliates: () => api.get('/admin/affiliates'),
  payout: (userId) => api.put(`/admin/users/${userId}/payout`),
  sendToAuthority: (appealId) => api.post(`/admin/appeals/${appealId}/send-to-authority`),
  getContactMessages: (params) => api.get('/admin/contact-messages', { params }),
  updateContactStatus: (id, status) => api.put(`/admin/contact-messages/${id}/status`, { status }),
  deleteContactMessage: (id) => api.delete(`/admin/contact-messages/${id}`),
};

export const facebookAPI = {
  // Connect/Disconnect
  connectPage: (data) => api.post('/facebook/connect-page', data),
  connectUser: (data) => api.post('/facebook/connect-user', data),
  getConnectedAccounts: () => api.get('/facebook/connected-accounts'),
  disconnectPage: (pageId) => api.delete(`/facebook/disconnect-page/${pageId}`),
  disconnectUser: (userId) => api.delete(`/facebook/disconnect-user/${userId}`),

  // Resumable Upload
  startUpload: (data) => api.post('/facebook/upload/start', data),
  transferUpload: (formData, params) => api.post('/facebook/upload/transfer', formData, { params, headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadStatus: (params) => api.get('/facebook/upload/status', { params }),

  // Chunked Upload
  chunkedStart: (data) => api.post('/facebook/upload/chunked/start', data),
  chunkedTransfer: (formData, params) => api.post('/facebook/upload/chunked/transfer', formData, { params, headers: { 'Content-Type': 'multipart/form-data' } }),
  chunkedFinish: (data) => api.post('/facebook/upload/chunked/finish', data),

  // Publish
  publishVideo: (data) => api.post('/facebook/video/publish', data),
  publishUploaded: (data) => api.post('/facebook/video/publish-uploaded', data),

  // Read
  getVideo: (videoId, params) => api.get(`/facebook/video/${videoId}`, { params }),
  getVideoLikes: (videoId, params) => api.get(`/facebook/video/${videoId}/likes`, { params }),
  getVideoComments: (videoId, params) => api.get(`/facebook/video/${videoId}/comments`, { params }),
  getVideoShares: (videoId, params) => api.get(`/facebook/video/${videoId}/sharedposts`, { params }),
  getVideoCaptions: (videoId, params) => api.get(`/facebook/video/${videoId}/captions`, { params }),

  // Local management
  listVideos: (params) => api.get('/facebook/videos', { params }),
  deleteVideo: (videoId, data) => api.delete(`/facebook/videos/${videoId}`, { data }),
};

export default api;
